import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  colorContentPlaceholder,
  colorStatusNeutralBoldest,
  colorStatusNoticeBoldest,
  colorStatusPositiveDefault,
} from '@duxton-web/design-tokens/themes/light';
import {
  CopyFilled,
  LaunchUniversal,
  ShowOutlined,
  TickMarkOutlined,
} from '@duxton-web/icons';
import {
  Alert,
  Button,
  Card,
  Divider,
  Flex,
  Modal,
  Typography,
} from '@duxton-web/ui';
import dayjs from 'dayjs';

import SegPInsightsWidget from 'components/form/trident-campaigns/SegPInsightsWidget';
import DataBox from 'omega-rtc/components/custom/DataBox';
import FlexColumn from 'omega-rtc/components/custom/FlexColumn';
import FlexRow from 'omega-rtc/components/custom/FlexRow';
import UCDStatusLabel from 'omega-rtc/components/form/ucd/common/UCDStatusLabel.jsx';
import SmartCampaignImagePreview from 'omega-rtc/components/form/ucd/SmartCampaignImagePreview';
import SmartCampaignLanguageBasedPreview from 'omega-rtc/components/form/ucd/SmartCampaignLanguageBasedPreview';
import { showControlGroup } from 'omega-rtc/config';
import {
  HUMAN_READ_DATE_FORMAT,
  PUSH_CHANNEL_ACTIONS,
  SMART_CAMPAIGN_CHANNELS,
  STATUS,
  TRIDENT_QUESTIONS_SLACK_CHANNEL_URL,
  VERTICALS_DISPLAY_NAMES,
} from 'omega-rtc/constants/ucd';
import { userTypeToSegmentType } from 'omega-rtc/data/trident-campaigns';
import { getPushChannelActionLabelBasedOnValue } from 'omega-rtc/utils/smart-campaign-utils';
import { getCountryNameFromCountryID, getCountryTimezonesFromCountryID } from 'omega-rtc/utils/ucd-utils';
import { formatTimezoneString, convertHoursInUTCToDisplayTime } from 'omega-rtc/utils/time-utils';

type SmartCampaignOverviewCardProps = {
  segment?: string;
  endDate?: string;
  cardTitle: string;
  vertical?: string;
  actionType: string;
  startDate?: string;
  actionURL?: string;
  projectID?: string;
  errorReason?: string; // info: only pass errorReason if viewer of the page === creator
  channelType?: string;
  isManualMode: boolean;
  campaignName?: string;
  campaignCode?: string;
  isPreviewMode?: boolean;
  primaryLanguage: string;
  campaignStatus?: string;
  controlGroupTime: string;
  campaignCreatedBy?: string;
  pushNotificationImage: any;
  selectedLanguages: string[];
  isMetricsAvailable?: boolean;
  metaDataForEachLanguages: any;
  cardStyle?: React.CSSProperties;
  isRecurrenceEnabled?: boolean;
  recurrenceWindowDays?: number;
  nextSubmissionTime?: string;
  hoursInUTC?: number[];
  audienceType?: string;
};

const SmartCampaignOverviewCard = ({
  segment,
  endDate,
  vertical,
  actionURL,
  cardStyle,
  startDate,
  actionType,
  projectID,
  errorReason,
  campaignCode,
  campaignName,
  campaignStatus,
  metaDataForEachLanguages,
  selectedLanguages,
  campaignCreatedBy,
  isMetricsAvailable,
  channelType,
  pushNotificationImage,
  controlGroupTime,
  cardTitle,
  primaryLanguage,
  isManualMode,
  isRecurrenceEnabled,
  recurrenceWindowDays,
  nextSubmissionTime,
  hoursInUTC,
  audienceType,

  isPreviewMode = false,
}: SmartCampaignOverviewCardProps) => {
  const [showInsights, setShowInsights] = useState(false);

  const projectView = useSelector((state: any) => state.ucd.projectView);
  const countries = useSelector((state: any) => state.countries.entities);

  const project = useMemo(() => {
    return (
      projectView && projectView.projectID === projectID && projectView.project
    );
  }, [projectView, projectID]);

  const countryID = useMemo(() => project && project.countryID, [project]);

  // Extract segment name and namespace for insights
  const segmentName = useMemo(() => {
    if (!segment) return '';
    const parts = segment.split('.');
    return parts.length > 1 ? parts[1] : segment;
  }, [segment]);

  const segmentNamespace = useMemo(() => {
    if (!segment) return 'shared';
    const parts = segment.split('.');
    return parts.length > 1 ? parts[0] : 'shared';
  }, [segment]);

  const isPendingAIP = useMemo(() => {
    const displayStatus = _.toUpper(campaignStatus || '');
    return displayStatus === STATUS.pendingAIP;
  }, [campaignStatus]);

  const isNextSubmissionTime = useMemo(() => {
    // Only show the message if the next submission time is after today and before the end date
    return (
      nextSubmissionTime &&
      dayjs(nextSubmissionTime).isAfter(dayjs()) &&
      dayjs(nextSubmissionTime).isBefore(dayjs(endDate))
    );
  }, [nextSubmissionTime, endDate]);

  const { formattedTimeWindow, formattedTimezone } = useMemo(() => {
    let formattedTimeWindow = '';
    let formattedTimezone = '';
    if (!hoursInUTC || hoursInUTC.length === 0) {
      return { formattedTimeWindow, formattedTimezone };
    }
    const timezones = getCountryTimezonesFromCountryID(countryID, countries);
    const offset = timezones && timezones.length > 0 ? timezones[0].offset : 0;
    formattedTimeWindow = convertHoursInUTCToDisplayTime(hoursInUTC, offset);
    formattedTimezone = formatTimezoneString(countryID, timezones[0]);
    return { formattedTimeWindow, formattedTimezone };
  }, [hoursInUTC, countryID, countries]);

  // Helper function to construct segment URL following campaign-analysis pattern
  const getSegmentUrl = (userType, segmentName) => {
    const parts = _.split(segmentName, '.');
    const hasNamespace = parts.length > 1;

    let namespace = '';
    let id = segmentName;

    if (hasNamespace) {
      namespace = `/${parts[0]}`;
      id = parts[1];
    }
    // replace non-breaking space with space only if id is a string
    if (typeof id === 'string') {
      id = id.replace(/\u00A0/g, ' ');
    }

    const segType = _.get(userTypeToSegmentType, userType, userType);
    return `${window.Grab.config.SEGMENTATION_PLATFORM_URI}/segments/${id}/${segType}${namespace}`;
  };

  // Helper function to format audience type
  const formatAudienceType = (type) => {
    if (type === 'local') return 'Locals';
    if (type === 'traveller') return 'Travellers';
    return type;
  };

  return (
    <Card title={cardTitle} style={cardStyle}>
      <FlexColumn>
        {/* error reason - not used in preview */}
        {!isPreviewMode && <ErrorMessageContainer errorReason={errorReason} />}

        {/* - Context */}
        <Typography.Title level={5} style={{ margin: 0 }}>
          Context
        </Typography.Title>
        <FlexRow>
          <DataBox label="Campaign name" content={campaignName} />
          <DataBox label="Project" content={project && project.name} />
        </FlexRow>
        {/* status of campaign and creator name is only shown if not in preview mode */}
        {!isPreviewMode && (
          <FlexRow>
            <DataBox
              label="Status"
              content={
                <Flex vertical gap={4} align="flex-start">
                  <Flex align="center" gap={8}>
                    <UCDStatusLabel
                      status={campaignStatus || ''}
                      start={startDate}
                      end={endDate}
                      labelSize={'medium'}
                    />
                    <TickMarkOutlined
                      style={{
                        color: isMetricsAvailable
                          ? colorStatusPositiveDefault
                          : colorContentPlaceholder,
                        fontSize: 24,
                      }}
                    />
                  </Flex>
                  {isPendingAIP &&
                    dayjs(dayjs()).isBefore(dayjs(startDate)) && (
                      <Typography.Text
                        style={{
                          fontSize: '12px',
                          color: 'rgba(0, 0, 0, 0.45)',
                        }}
                      >
                        Campaign will be processed and activated on{' '}
                        {dayjs(startDate)
                          .subtract(1, 'day')
                          .format(HUMAN_READ_DATE_FORMAT)}
                      </Typography.Text>
                    )}
                </Flex>
              }
            />
            <DataBox
              label="Creator"
              content={(campaignCreatedBy || '').split('@')[0]}
            />
          </FlexRow>
        )}
        <FlexRow>
          <DataBox
            label="Vertical"
            content={VERTICALS_DISPLAY_NAMES[vertical]}
          />
          <DataBox
            label="Campaign code (generated)"
            content={campaignCode}
            enableContentCopy
          />
        </FlexRow>
        <FlexRow>
          <DataBox
            label="Recurring campaign"
            content={
              isRecurrenceEnabled ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Flex vertical gap={4} align="flex-start">
                    Yes
                    <Typography.Text
                      style={{
                        fontSize: '12px',
                        color: 'rgba(0, 0, 0, 0.45)',
                      }}
                    >
                      Repeats every {recurrenceWindowDays || 3} day(s).{isNextSubmissionTime && ` Next window starts on ${nextSubmissionTime}`}
                    </Typography.Text>
                  </Flex>
                </div>
              ) : (
                'No'
              )
            }
          />
          <div style={{ flex: 1 }} />
        </FlexRow>
        <Divider style={{ margin: 0 }} />

        {/* User */}
        <Typography.Title level={5} style={{ margin: 0 }}>
          User
        </Typography.Title>
        <FlexRow>
          <DataBox
            label="Country"
            content={getCountryNameFromCountryID(countryID, countries)}
          />
          <DataBox
            label="Segment ID"
            content={
              <Flex vertical gap={8}>
                {segment}
                <Flex gap={16} wrap="wrap">
                  <Typography.Link
                    onClick={() => setShowInsights(true)}
                    style={{ cursor: 'pointer' }}
                  >
                    Preview segment <ShowOutlined />
                  </Typography.Link>
                  <Typography.Link
                    href={getSegmentUrl('passenger', segment)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open CDP <LaunchUniversal />
                  </Typography.Link>
                </Flex>
              </Flex>
            }
          />
        </FlexRow>
        {audienceType && _.get(window.Grab.config, 'PORTAL_CONFIG.holos.enabledTargetAudience', false) && (
          <FlexRow>
            <DataBox
              label="Target Audience"
              content={formatAudienceType(audienceType)}
            />
            <div style={{ flex: 1 }} />
          </FlexRow>
        )}

        {/* Segment Insights Modal */}
        <Modal
          title="Segment Insights"
          open={showInsights}
          onCancel={() => setShowInsights(false)}
          footer={null}
          width={800}
          styles={{
            body: { padding: '20px 0' },
          }}
        >
          {segment && (
            <SegPInsightsWidget
              segmentID={segmentName}
              namespace={segmentNamespace}
              onDataLoaded={(data) => {
                // Handle insights data if needed
                console.log('Segment insights loaded:', data);
              }}
            />
          )}
        </Modal>
        <Divider style={{ margin: 0 }} />

        {/* - Time */}
        <Typography.Title level={5} style={{ margin: 0 }}>
          Campaign period
        </Typography.Title>
        <FlexRow>
          <DataBox
            label="Start date"
            content={
              startDate && dayjs(startDate).format(HUMAN_READ_DATE_FORMAT)
            }
          />
          <DataBox
            label="End date"
            content={endDate && dayjs(endDate).format(HUMAN_READ_DATE_FORMAT)}
          />
        </FlexRow>
        {formattedTimeWindow && (
          <FlexRow>
            <DataBox
              label="Timezone"
              content={formattedTimezone}
            />
            <DataBox
              label="Time window"
              content={formattedTimeWindow}
            />
          </FlexRow>
        )}
        {showControlGroup && (
          <FlexRow>
            <DataBox
              label="Control group trigger time"
              content={controlGroupTime}
            />
          </FlexRow>
        )}
        <Divider style={{ margin: 0 }} />

        {/* - Content Variants */}
        <Typography.Title level={5} style={{ margin: 0 }}>
          Content
        </Typography.Title>
        {channelType === SMART_CAMPAIGN_CHANNELS.push &&
          pushNotificationImage && (
            <SmartCampaignImagePreview imageURL={pushNotificationImage} />
          )}
        <SmartCampaignLanguageBasedPreview
          isManualMode={isManualMode}
          primaryLanguage={primaryLanguage}
          channel={channelType}
          isPreviewMode={isPreviewMode}
          metaDataForEachLanguages={metaDataForEachLanguages}
          selectedLanguages={selectedLanguages}
        />
        {channelType === SMART_CAMPAIGN_CHANNELS.push && (
          <React.Fragment>
            <Divider style={{ margin: 0 }} />
            <Typography.Title level={5} style={{ margin: 0 }}>
              Action
            </Typography.Title>
            <FlexRow>
              <DataBox
                label="Action"
                content={getPushChannelActionLabelBasedOnValue(actionType)}
              />
              {actionType !== PUSH_CHANNEL_ACTIONS.grabApp && (
                <DataBox
                  label="URL"
                  content={actionURL || ' No url is entered'}
                  isContentEmpty={!actionURL}
                />
              )}
            </FlexRow>
          </React.Fragment>
        )}
      </FlexColumn>
    </Card>
  );
};

const ErrorMessageContainer = ({ errorReason }) => {
  if (!errorReason) {
    return <React.Fragment />;
  }

  return (
    <Alert
      showIcon
      type="warning"
      message="Failed to deliver the campaign"
      style={{ padding: 16, gap: 2 }}
      action={
        <Flex align="center" style={{ paddingTop: 20 }}>
          <Button
            type="text"
            size="small"
            onClick={() => window.open(TRIDENT_QUESTIONS_SLACK_CHANNEL_URL)}
          >
            Ask for help
          </Button>
        </Flex>
      }
      description={
        <React.Fragment>
          <Typography.Body
            level={3}
            style={{ color: colorStatusNoticeBoldest }}
          >
            The system failed to deliver the campaign, so we have reverted it
            back to draft. Please review the setup once more and then retry.
          </Typography.Body>

          <Typography.Body
            level={3}
            style={{ color: colorStatusNeutralBoldest, marginLeft: 5 }}
            copyable={{
              text: errorReason,
              icon: <CopyFilled style={{ color: colorStatusNeutralBoldest }} />,
            }}
          >
            Copy error reason
          </Typography.Body>
        </React.Fragment>
      }
    />
  );
};

export default SmartCampaignOverviewCard;
